import { useState } from 'react';

import { Person } from '@api/lendingAPI/CustomerStateAPIHelper';
import { tracking } from '@iwoca/frontend-tracking-library';
import { IconPlus, IconQuestionMarkOutline } from '@iwoca/icons';
import { Button, ErrorMessage, Text } from '@iwoca/orion';
import { maintainStateKey } from '@iwRouter/LocationHelper';
import { FormattedMessage } from 'react-intl';
import { useHistory, useLocation } from 'react-router-dom';

import { useConfirmSavedShareholder } from './useConfirmSavedShareholder';
import {
    convertSuggestionToPerson,
    filterOutAlreadySavedSuggestions,
    hasMissingSuggestionShareholderRole
} from '../../../../../funding/fundingSteps/majorShareholderCheckAction/mshModal/MSHForm.helpers';
import { TSuggestion } from '../../../../../funding/fundingSteps/majorShareholderCheckAction/mshModal/MSHForm.types';
import {
    useAssociatedPeopleSuggestions,
    useDismissSuggestion
} from '../../../../../funding/fundingSteps/majorShareholderCheckAction/mshModal/useAssociatedPeopleSuggestions';
import { InnerLayout } from '../../InnerLayout/InnerLayout';
import { useRequirementsProgress } from '../../useRequirementsProgress';
import { ShareholderCard } from '../ShareholderCard/ShareholderCard';
import { ShareholderForm } from '../ShareholderForm/ShareholderForm';
import { useGetShareholdersData } from '../useGetShareholdersData';

/**
 * Controls the ShareholderForm's visibility and ensures only one form is open at a time. Calling
 * any transition (editPerson, confirmSuggestion, addPerson) closes the previous form. closeForm
 * resets back to 'closed'.
 * - 'closed': no form visible, cards and buttons shown normally.
 * - 'add': blank form rendered below all cards for adding a new person.
 * - 'edit': pre-filled form rendered in place of the card being edited, carries the person data.
 * - 'confirm': pre-filled form rendered in place of a suggestion card, carries the suggestion data.
 */
type TFormState =
    | { type: 'closed' }
    | { type: 'add' }
    | { type: 'edit'; person: Person }
    | { type: 'confirm'; suggestion: TSuggestion };

const useShareholderFormState = () => {
    const [formState, setFormState] = useState<TFormState>({ type: 'closed' });
    return {
        formState,
        editPerson: (person: Person) => setFormState({ type: 'edit', person }),
        confirmSuggestion: (suggestion: TSuggestion) =>
            setFormState({ type: 'confirm', suggestion }),
        addPerson: () => setFormState({ type: 'add' }),
        closeForm: () => setFormState({ type: 'closed' })
    };
};

export const ShareholdersPage = () => {
    const people = useGetShareholdersData();
    const { suggestions } = useAssociatedPeopleSuggestions();
    const { mutate: dismissSuggestion } = useDismissSuggestion();
    const history = useHistory();
    const { search } = useLocation();
    const { formState, editPerson, confirmSuggestion, addPerson, closeForm } =
        useShareholderFormState();
    const progress = useRequirementsProgress();
    const isFormOpen = formState.type !== 'closed';
    const unsavedSuggestions = filterOutAlreadySavedSuggestions(
        suggestions,
        people
    );
    const suggestedPeople = unsavedSuggestions.map(convertSuggestionToPerson);
    const { confirmShareholder, confirmingPersonUid, hasError } =
        useConfirmSavedShareholder();

    return (
        <InnerLayout
            requirementType="MAJOR_SHAREHOLDER_CHECK"
            title={
                <FormattedMessage
                    id="_.funding_requirements.shareholders_page.title"
                    defaultMessage="Add your directors and major shareholders"
                />
            }
            subheading={
                <FormattedMessage
                    id="_.funding_requirements.shareholders_page.subheading"
                    defaultMessage="We need details of the directors and major shareholders with 25% or more shares in your business."
                />
            }
        >
            <div className="mt-s flex items-start gap-m rounded-xs border border-secondary-80 bg-secondary-95 p-s text-s">
                <IconQuestionMarkOutline className="mt-xs shrink-0 text-l" />
                <Text className="text-s">
                    <FormattedMessage
                        id="_.funding_requirements.shareholders_page.info"
                        defaultMessage="We need this to protect against fraud. We are required to run anti-money laundering checks."
                    />
                </Text>
            </div>

            <Text className="mt-l text-m">
                <FormattedMessage
                    id="_.funding_requirements.shareholders_page.credit_score_note"
                    defaultMessage="(don't worry, this won't affect anyone's credit score)"
                />
            </Text>

            {hasError && (
                <ErrorMessage className="mt-m">
                    <FormattedMessage
                        id="_.shareholder_form.submit_error"
                        defaultMessage="Sorry there was an error, please try again"
                    />
                </ErrorMessage>
            )}

            <div className="mt-m">
                {people
                    .filter(
                        (person) => person.isDirector || person.isShareholder
                    )
                    .map((person) => {
                        const needsConfirmation =
                            hasMissingSuggestionShareholderRole(
                                person,
                                suggestions
                            );
                        return formState.type === 'edit' &&
                            formState.person.uid === person.uid ? (
                            <ShareholderForm
                                key={person.uid}
                                onClose={closeForm}
                                personBeingEdited={formState.person}
                            />
                        ) : (
                            <ShareholderCard
                                key={person.uid}
                                person={person}
                                status={
                                    needsConfirmation
                                        ? 'needs_confirming'
                                        : 'confirmed'
                                }
                                showAsShareholder={needsConfirmation}
                                onEdit={() => editPerson(person)}
                                onConfirm={() => confirmShareholder(person)}
                                isConfirming={
                                    confirmingPersonUid === person.uid
                                }
                                disabled={isFormOpen}
                            />
                        );
                    })}
                {suggestedPeople.map((person, index) =>
                    formState.type === 'confirm' &&
                    formState.suggestion === unsavedSuggestions[index] ? (
                        <ShareholderForm
                            key={`suggestion-${index}`}
                            onClose={closeForm}
                            suggestionBeingConfirmed={formState.suggestion}
                        />
                    ) : (
                        <ShareholderCard
                            key={`suggestion-${index}`}
                            person={person as Person}
                            status="incomplete"
                            onEdit={() =>
                                confirmSuggestion(unsavedSuggestions[index])
                            }
                            onRemove={() => {
                                tracking.ampli.fundingRequirementsSuggestedShareholderRemoved(
                                    { version: '2.0' }
                                );
                                dismissSuggestion(unsavedSuggestions[index]);
                            }}
                            disabled={isFormOpen}
                        />
                    )
                )}
            </div>

            {formState.type === 'add' ? (
                <ShareholderForm onClose={closeForm} />
            ) : formState.type === 'closed' ? (
                <Button
                    variant="tertiary"
                    className="mt-m h-auto p-0"
                    onClick={addPerson}
                    type="button"
                >
                    <FormattedMessage
                        id="_.funding_requirements.shareholders_page.add_person"
                        defaultMessage="Add a director or major shareholder"
                    />
                    <IconPlus />
                </Button>
            ) : null}

            {formState.type === 'closed' && (
                <div className="my-xl border-t border-structure-80">
                    <Text className="my-xl text-m">
                        <FormattedMessage
                            id="_.funding_requirements.shareholders_page.confirm_question"
                            defaultMessage="Are you finished adding all your business' major shareholders?"
                        />
                    </Text>

                    <Button
                        variant="primary-rounded"
                        onClick={() => {
                            tracking.ampli.fundingRequirementsShareholderStepCompleted(
                                { version: '2.0' }
                            );
                            tracking.ampli.fundingRequirementCompleted({
                                requirement: 'major_shareholder_check',
                                progress: {
                                    ...progress,
                                    major_shareholder_check: true
                                },
                                version: '2.0'
                            });
                            history.push(
                                maintainStateKey({
                                    pathname: '/funding-requirements/',
                                    search
                                })
                            );
                        }}
                        type="button"
                    >
                        <FormattedMessage
                            id="_.funding_requirements.shareholders_page.confirm_button"
                            defaultMessage="Confirm and return to list"
                        />
                    </Button>
                </div>
            )}
        </InnerLayout>
    );
};
